import { createI18n } from 'vue-i18n'
import commonZhLocale from './common/zh_cn'
import commonTcLocale from './common/zh_tc'
import commonEnLocale from './common/en_us'
import { getLangType } from '@fs/utils'

const { VUE_APP_ENV = 'sit' } = import.meta.env

export const kEnglishEnabled = VUE_APP_ENV === 'sit'

/**
 * 统一语言代码：
 * - zhCn: 简体中文
 * - zhTc: 繁体中文
 * - en: 英文
 */
type LangType = 'zhCn' | 'zhTc' | 'en'

/**
 * Vue3 i18n 初始化参数。
 *
 * 设计目标：
 * 1) 允许业务页面只传“页面级语言包”，与 common 公共包自动合并。
 * 2) 支持外部完全覆盖（zhCn/zhTc/en）以适配更复杂场景。
 * 3) 显式配置英文开关，避免依赖环境变量带来的隐式行为。
 */
interface InitI18nOptions {
  zhLocale?: Record<string, unknown>
  tcLocale?: Record<string, unknown>
  enLocale?: Record<string, unknown>
  zhCn?: Record<string, unknown>
  zhTc?: Record<string, unknown>
  en?: Record<string, unknown>
  getLang?: () => LangType
  defaultLang?: LangType
}

/**
 * 将多种语言写法归一化为系统内部语言码。
 * 例如：zh-cn / zh_cn / zhhans -> zhCn。
 */
const normalizeLang = (rawLang: string): LangType | undefined => {
  const lang = rawLang.trim().replace(/[_-]/g, '').toLowerCase()
  if (lang === 'zhcn' || lang === 'zhhans') {
    return 'zhCn'
  }
  if (lang === 'zhtc' || lang === 'zhtw' || lang === 'zhhk' || lang === 'zhhant') {
    return 'zhTc'
  }
  if (lang === 'en' || lang === 'enus' || lang === 'engb') {
    return 'en'
  }
  return undefined
}

/**
 * 从 URL 查询参数读取语言（支持 lang / locale）。
 * URL 优先级高于 localStorage，便于外链、活动页和灰度实验直接指定语言。
 */
const readLangFromUrl = (): LangType | undefined => {
  const search = globalThis.location?.search
  if (!search) {
    return undefined
  }
  const params = new URLSearchParams(search)
  return normalizeLang(params.get('lang') ?? params.get('locale') ?? '')
}

/**
 * 从浏览器本地缓存读取语言。
 * 仅在 URL 未指定时生效，用于记住用户上次选择。
 */
const readLangFromStorage = (storageKey: string): LangType | undefined => {
  const lang = globalThis.localStorage?.getItem(storageKey) ?? ''
  return normalizeLang(lang)
}

/**
 * 确保最终语言一定在当前“可用语言集合”内。
 * 例如英文关闭时，任何 en 都会被降级到 defaultLang（若 defaultLang=en，再降到 zhCn）。
 */
const normalizeSupportedLang = (
  lang: LangType,
  enableEnglish: boolean,
  defaultLang: LangType
): LangType => {
  if (lang !== 'en') {
    return lang
  }
  if (enableEnglish) {
    return lang
  }
  return defaultLang === 'en' ? 'zhCn' : defaultLang
}

/**
 * 统一语言决策入口。
 *
 * 优先级：
 * 1) getLang（外部主动传入）
 * 2) URL 参数
 * 3) localStorage
 * 4) defaultLang
 */
const resolveLang = (options: {
  getLang?: () => LangType
  defaultLang: LangType
  enableEnglish: boolean
  storageKey?: string
}): LangType => {
  const { getLang, defaultLang, enableEnglish, storageKey = 'lang' } = options

  const fromGetter = getLang?.() ?? getLangType()
  if (fromGetter) {
    return normalizeSupportedLang(fromGetter, enableEnglish, defaultLang)
  }

  const fromUrl = readLangFromUrl()
  if (fromUrl) {
    return normalizeSupportedLang(fromUrl, enableEnglish, defaultLang)
  }

  const fromStorage = readLangFromStorage(storageKey)
  if (fromStorage) {
    return normalizeSupportedLang(fromStorage, enableEnglish, defaultLang)
  }

  return normalizeSupportedLang(defaultLang, enableEnglish, defaultLang)
}

/**
 * 持久化最终生效语言。
 * 写入失败不影响运行（如隐私模式、受限 WebView）。
 */
const persistLang = (lang: LangType, storageKey: string = 'lang') => {
  try {
    globalThis.localStorage?.setItem(storageKey, lang)
  } catch {
    // Ignore localStorage write errors (private mode or restricted environments).
  }
}

/**
 * 创建 Vue3 i18n 实例，并完成：
 * - 页面语言包与 common 包合并
 * - 语言决策（URL / 缓存 / 默认值）
 * - 英文开关控制
 * - 语言持久化
 */
export default function initI18n(options: InitI18nOptions = {}) {
  let { zhCn, zhTc, en } = options
  const { zhLocale = {}, tcLocale = {}, enLocale = {}, getLang, defaultLang = 'zhCn' } = options

  zhCn = zhCn ?? {
    // 页面语言优先，common 兜底。
    ...zhLocale,
    ...commonZhLocale,
  }

  zhTc = zhTc ?? {
    // 页面语言优先，common 兜底。
    ...tcLocale,
    ...commonTcLocale,
  }

  en = en ?? {
    // 页面语言优先，common 兜底。
    ...enLocale,
    ...commonEnLocale,
  }

  const messages: Record<string, Record<string, unknown>> = {
    zhCn,
    zhTc,
    // en,
  }

  // if (!enableEnglish) {
  //   // 英文关闭时，不向 i18n 实例注册 en，避免被外部误切换。
  //   messages = { zhCn, zhTc }
  // }
  if (kEnglishEnabled) {
    messages.en = en
  }

  // fallback 也要经过可用语言校验，保证配置始终有效。
  const fallbackLocale = normalizeSupportedLang(defaultLang, kEnglishEnabled, defaultLang)

  const locale = resolveLang({
    getLang,
    defaultLang: fallbackLocale,
    enableEnglish: kEnglishEnabled,
    // storageKey,
  })

  persistLang(locale)

  // // 初始化vant组件多语言
  // vantLocales()

  return createI18n({
    // legacy 模式可兼容模板里 $t 与现有调用习惯。
    legacy: false,
    locale,
    fallbackLocale,
    messages: messages as never,
  })
}
